/**
 * Advanced Field Rules — shared evaluator (REFERENCE IMPLEMENTATION).
 *
 * Pure, deterministic, dependency-free. The C# port (RuleEvaluator.cs) and the
 * Mobile port (aQRate-Mobile/components/forms/ruleEvaluator.ts) MUST stay
 * behaviourally identical to this file. The conformance fixture
 * `rule-evaluator-vectors.json` is replayed against all three to catch drift.
 *
 * --- DSL -------------------------------------------------------------------
 * Each field carries `rules: FieldRule[]`. A rule lives on the field it acts on
 * (the target) and references other fields by their stable `key`.
 *
 *   FieldRule = {
 *     id: string,
 *     effect: 'visibility' | 'cascade' | 'format',
 *     when?: ConditionGroup,                    // visibility, format
 *     action?: 'show' | 'hide',                 // visibility (default 'show')
 *     controlledBy?: string,                    // cascade — key of controlling field
 *     style?: { bg?, fg?, flag?, icon? },       // format
 *   }
 *   ConditionGroup = { logic: 'and' | 'or', conditions: Condition[] }
 *   Condition = { field: string, op: string, value?: string, valueTo?: string, values?: string[] }
 *
 * --- Effects ---------------------------------------------------------------
 *   visibility : when `when` matches, the field is shown ('show') or hidden ('hide').
 *   cascade    : filters this field's `options` to those whose `group` equals the
 *                current value of `controlledBy`. Empty controller value => no filter.
 *   format     : when `when` matches, applies `style`. Multiple format rules on one
 *                field are evaluated top-to-bottom; first match wins.
 * --------------------------------------------------------------------------
 */

/** Canonical operator vocabulary (mirrors Analytics/FieldFilterDto.cs). */
export const RULE_OPERATORS = [
  'equals', 'notEquals', 'gt', 'gte', 'lt', 'lte', 'between',
  'contains', 'startsWith', 'in', 'isEmpty', 'isNotEmpty', 'before', 'after',
];

/** Canonical effect vocabulary. */
export const RULE_EFFECTS = ['visibility', 'cascade', 'format'];

const NUMBER_RE = /^[+-]?(\d+\.?\d*|\.\d+)$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/;
const TIME_RE = /^(\d{1,2}):(\d{2})/;

/** Normalise any value to a stable string (booleans lowercased, arrays joined). */
function normStr(x) {
  if (x === null || x === undefined) return '';
  if (typeof x === 'boolean') return x ? 'true' : 'false';
  if (typeof x === 'number') return Number.isFinite(x) ? String(x) : '';
  if (Array.isArray(x)) return x.map(normStr).join(',');
  return String(x);
}

/** Parse a plain decimal number, or null. Hex/exponent/Infinity are rejected. */
function tryNumber(x) {
  if (typeof x === 'number') return Number.isFinite(x) ? x : null;
  if (typeof x === 'string') {
    const t = x.trim();
    if (NUMBER_RE.test(t)) {
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

/** Parse an ISO-8601 date/datetime to epoch milliseconds, or null. No-zone => UTC. */
function tryInstant(x) {
  if (typeof x !== 'string') return null;
  const t = x.trim();
  if (DATE_RE.test(t)) {
    const ms = Date.parse(t + 'T00:00:00Z');
    return Number.isNaN(ms) ? null : ms;
  }
  if (DATETIME_RE.test(t)) {
    const s = /(Z|[+-]\d{2}:\d{2})$/.test(t) ? t : t + 'Z';
    const ms = Date.parse(s);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

/** Parse a wall-clock "HH:MM" to minutes-of-day, or null. */
function tryMinutesOfDay(x) {
  if (typeof x !== 'string') return null;
  const m = x.trim().match(TIME_RE);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/** True for null/undefined, blank strings, and empty arrays. */
function isEmptyValue(x) {
  if (x === null || x === undefined) return true;
  if (typeof x === 'string') return x.trim() === '';
  if (Array.isArray(x)) return x.length === 0;
  return false;
}

/** equals: numeric when both sides are numeric, otherwise normalised-string. */
function looseEquals(actual, value) {
  const na = tryNumber(actual);
  const nv = tryNumber(value);
  if (na !== null && nv !== null) return na === nv;
  return normStr(actual) === normStr(value);
}

/** Evaluate a single condition against the form values. */
function evaluateCondition(cond, values) {
  if (!cond || typeof cond !== 'object') return false;
  const actual = values ? values[cond.field] : undefined;
  switch (cond.op) {
    case 'equals':
      return looseEquals(actual, cond.value);
    case 'notEquals':
      return !looseEquals(actual, cond.value);
    case 'gt': {
      const a = tryNumber(actual); const b = tryNumber(cond.value);
      return a !== null && b !== null && a > b;
    }
    case 'gte': {
      const a = tryNumber(actual); const b = tryNumber(cond.value);
      return a !== null && b !== null && a >= b;
    }
    case 'lt': {
      const a = tryNumber(actual); const b = tryNumber(cond.value);
      return a !== null && b !== null && a < b;
    }
    case 'lte': {
      const a = tryNumber(actual); const b = tryNumber(cond.value);
      return a !== null && b !== null && a <= b;
    }
    case 'between': {
      const a = tryNumber(actual);
      const lo = tryNumber(cond.value);
      const hi = tryNumber(cond.valueTo);
      if (a === null || lo === null || hi === null) return false;
      return a >= lo && a <= hi;
    }
    case 'contains': {
      if (Array.isArray(actual)) {
        return actual.some((el) => normStr(el) === normStr(cond.value));
      }
      return normStr(actual).includes(normStr(cond.value));
    }
    case 'startsWith':
      return normStr(actual).startsWith(normStr(cond.value));
    case 'in':
      return Array.isArray(cond.values) && cond.values.some((v) => looseEquals(actual, v));
    case 'isEmpty':
      return isEmptyValue(actual);
    case 'isNotEmpty':
      return !isEmptyValue(actual);
    case 'before': {
      const a = tryInstant(actual); const b = tryInstant(cond.value);
      if (a !== null && b !== null) return a < b;
      const ta = tryMinutesOfDay(actual); const tb = tryMinutesOfDay(cond.value);
      if (ta !== null && tb !== null) return ta < tb;
      return false;
    }
    case 'after': {
      const a = tryInstant(actual); const b = tryInstant(cond.value);
      if (a !== null && b !== null) return a > b;
      const ta = tryMinutesOfDay(actual); const tb = tryMinutesOfDay(cond.value);
      if (ta !== null && tb !== null) return ta > tb;
      return false;
    }
    default:
      return false;
  }
}

/** Evaluate a condition group (AND/OR). A missing/empty group is vacuously true. */
function evaluateGroup(group, values) {
  if (!group || !Array.isArray(group.conditions) || group.conditions.length === 0) {
    return true;
  }
  const results = group.conditions.map((c) => evaluateCondition(c, values));
  return group.logic === 'or' ? results.some(Boolean) : results.every(Boolean);
}

/** Copy only the known, defined style keys (mirrors C# null-omitting serialization). */
function cleanStyle(style) {
  const out = {};
  if (style && typeof style === 'object') {
    if (style.bg !== undefined && style.bg !== null) out.bg = style.bg;
    if (style.fg !== undefined && style.fg !== null) out.fg = style.fg;
    if (style.flag !== undefined && style.flag !== null) out.flag = style.flag;
    if (style.icon !== undefined && style.icon !== null) out.icon = style.icon;
  }
  return out;
}

/**
 * Evaluate every field's rules against the current form values.
 *
 * @param {Array<{key:string, options?:Array<{value:string,group?:string}>, rules?:Array}>} fields
 * @param {Object<string,*>} values  Map of fieldKey -> current value.
 * @returns {{
 *   hiddenFieldKeys: string[],
 *   optionFilters: Object<string,string[]>,
 *   fieldStyles: Object<string,{bg?:string,fg?:string,flag?:string,icon?:string}>
 * }}
 */
export function evaluateRules(fields, values) {
  const hidden = new Set();
  const optionFilters = {};
  const fieldStyles = {};
  const vals = values && typeof values === 'object' ? values : {};

  if (Array.isArray(fields)) {
    for (const field of fields) {
      if (!field || !field.key || !Array.isArray(field.rules)) continue;
      for (const rule of field.rules) {
        if (!rule || typeof rule !== 'object') continue;

        if (rule.effect === 'visibility') {
          const matched = evaluateGroup(rule.when, vals);
          const visible = (rule.action === 'hide') ? !matched : matched;
          if (!visible) hidden.add(field.key);
        } else if (rule.effect === 'cascade') {
          if (!rule.controlledBy) continue;
          const controllerValue = vals[rule.controlledBy];
          if (isEmptyValue(controllerValue)) continue; // empty controller => show all
          const wanted = normStr(controllerValue);
          const allowed = Array.isArray(field.options)
            ? field.options
                .filter((o) => o && normStr(o.group) === wanted)
                .map((o) => o.value)
            : [];
          optionFilters[field.key] = allowed;
        } else if (rule.effect === 'format') {
          if (Object.prototype.hasOwnProperty.call(fieldStyles, field.key)) continue; // first match wins
          if (evaluateGroup(rule.when, vals)) {
            fieldStyles[field.key] = cleanStyle(rule.style);
          }
        }
      }
    }
  }

  return {
    hiddenFieldKeys: Array.from(hidden).sort(),
    optionFilters,
    fieldStyles,
  };
}
